import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { postCreateTeam, type CreateTeamBody } from '@/api/modules/team';
import { GooglePlacesAutocomplete } from '@/components/ui/GooglePlacesAutocomplete';
import { CountrySelect } from '@/components/ui/CountrySelect';
import {
  AUTH_TOKEN_KEY,
  MAX_TEAMS_PER_OPERATOR,
  canActivateFreeTrial,
  canCreateTeam,
  canJoinAnotherTeam,
  fetchSystemUserInfo,
  hasCreatedOwnTeam,
  showApiResultModal,
  type SystemUserInfo,
} from '@/api';
import { InlineFieldError } from '@/components/ui/FormFeedback';
import { SubpageHeader } from '@/components/ui/SubpageHeader';
import {
  DEFAULT_TEAM_COUNTRY,
  type TeamCountryCode,
} from '@/constants/teamCountries';
import { validateTeamPhone } from '@/lib/teamPhone';
import TeamLogoUploadPage from '@/pages/team/settings/logo/page';

type FieldErrors = Partial<Pick<CreateTeamBody, 'name' | 'phone' | 'email'>>;

const inputClassName =
  'w-full h-8 px-2.5 rounded-md border border-gray-200 text-xs font-normal outline-none focus:border-green-500 disabled:bg-gray-50 disabled:text-gray-400 placeholder:font-normal placeholder:text-xs placeholder:text-gray-400';

function validate(body: CreateTeamBody): FieldErrors {
  const e: FieldErrors = {};
  if (!body.name.trim()) e.name = 'Team name is required.';
  const phoneError = validateTeamPhone(body.phone ?? '', body.country, false);
  if (phoneError) e.phone = phoneError;
  const em = body.email?.trim() ?? '';
  if (em && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) e.email = 'Please enter a valid email.';
  return e;
}

export default function CreateTeamPage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<SystemUserInfo | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [editingLogo, setEditingLogo] = useState(false);
  const [form, setForm] = useState<CreateTeamBody>({
    name: '',
    logo_path: '',
    phone: '',
    email: '',
    address: '',
    country: DEFAULT_TEAM_COUNTRY,
    team_profile: '',
    state: 1,
  });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(AUTH_TOKEN_KEY)) {
      setProfileLoading(false);
      return;
    }

    let cancelled = false;
    fetchSystemUserInfo()
      .then((info) => {
        if (cancelled) return;
        setProfile(info);
      })
      .catch(() => {
        if (!cancelled) navigate('/me', { replace: true });
      })
      .finally(() => {
        if (!cancelled) setProfileLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const update = (field: keyof CreateTeamBody, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field in fieldErrors) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const updateCountry = (code: TeamCountryCode) => {
    setForm((prev) => ({
      ...prev,
      country: code,
      // Clear address when country changes so suggestions stay in-scope
      address: prev.country === code ? prev.address : '',
    }));
  };

  const handleSubmit = async () => {
    if (!canCreateTeam(profile)) return;

    const verr = validate(form);
    setFieldErrors(verr);
    if (Object.keys(verr).length > 0) return;

    setSubmitting(true);
    const result = await postCreateTeam(form);
    setSubmitting(false);

    showApiResultModal({
      message: result.message,
      success: !result.error,
      onDismiss: result.error ? undefined : () => navigate('/team', { replace: true }),
    });
  };

  const trialEligible = canActivateFreeTrial(profile);
  const createBlocked =
    !profileLoading &&
    profile != null &&
    !canCreateTeam(profile);
  const formDisabled = profileLoading || createBlocked;

  if (editingLogo) {
    return (
      <TeamLogoUploadPage
        draftOnly
        onDraftSuccess={(logoPath) => {
          update('logo_path', logoPath);
          setEditingLogo(false);
        }}
        onDraftBack={() => setEditingLogo(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <SubpageHeader
        title="Create Team"
        subtitle={`Create one team of your own; up to ${MAX_TEAMS_PER_OPERATOR} teams per account`}
        onBack={() => navigate('/me')}
      />

      <div
        className={`px-4 pt-3 space-y-2.5 ${
          !profileLoading && !createBlocked
            ? 'pb-[calc(5.25rem+env(safe-area-inset-bottom))]'
            : 'pb-6'
        }`}
      >
        {profileLoading ? (
          <div className="text-center py-10 text-gray-400 text-sm">
            <i className="ri-loader-4-line text-2xl animate-spin text-green-500 block mx-auto mb-2" />
            Loading...
          </div>
        ) : createBlocked ? (
          <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-2.5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-amber-600 text-lg" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-gray-900 font-semibold text-sm">Cannot create a team here</div>
                <p className="text-gray-500 text-xs mt-1 leading-relaxed">
                  {hasCreatedOwnTeam(profile)
                    ? 'You have already used your one-time team creation for this account.'
                    : `You have reached the limit of ${MAX_TEAMS_PER_OPERATOR} teams.`}
                  {' '}
                  You can still join another team with an invite code if you have room.
                </p>
              </div>
            </div>
            <button
              type="button"
              disabled={!canJoinAnotherTeam(profile)}
              onClick={() => navigate('/me/team/join')}
              className="w-full h-8 rounded-md border border-green-500 text-green-600 font-semibold text-sm cursor-pointer hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
            >
              <i className="ri-login-circle-line text-base" />
              Join Team
            </button>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-2.5">
              <button
                type="button"
                disabled={formDisabled}
                onClick={() => setEditingLogo(true)}
                className="w-full flex items-center gap-3 cursor-pointer text-left disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <div className="relative w-14 h-14 rounded-lg bg-green-50 border-2 border-dashed border-green-200 flex items-center justify-center overflow-hidden group flex-shrink-0">
                  {form.logo_path?.trim() ? (
                    <img
                      src={form.logo_path}
                      alt="Team logo"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <i className="ri-image-add-line text-green-500 text-xl" />
                  )}
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-camera-line text-white text-sm" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-gray-900 font-normal text-sm">Team Logo</div>
                  <div className="text-gray-400 text-xs mt-0.5 leading-snug">
                    Used on invoices · square (1:1) required
                  </div>
                  <div className="text-green-600 text-xs mt-1 inline-flex items-center gap-1">
                    <i className="ri-scissors-cut-line" />
                    {form.logo_path?.trim() ? 'Change logo' : 'Upload & crop'}
                  </div>
                </div>
                <i className="ri-arrow-right-s-line text-gray-300 text-xl flex-shrink-0" />
              </button>

              <div>
                <label className="text-gray-700 text-xs font-normal mb-1 block">
                  Team name <span className="text-red-500">*</span>
                </label>
                <input
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  placeholder="e.g. Sydney Field Services"
                  disabled={formDisabled}
                  className={inputClassName}
                />
                <InlineFieldError message={fieldErrors.name} />
              </div>

              <div>
                <label className="text-gray-700 text-xs font-normal mb-1 block">Phone</label>
                <input
                  value={form.phone ?? ''}
                  onChange={(e) => update('phone', e.target.value)}
                  placeholder="Contact phone"
                  disabled={formDisabled}
                  aria-invalid={!!fieldErrors.phone}
                  className={inputClassName}
                />
                <InlineFieldError message={fieldErrors.phone} />
              </div>

              <div>
                <label className="text-gray-700 text-xs font-normal mb-1 block">Email</label>
                <input
                  type="email"
                  value={form.email ?? ''}
                  onChange={(e) => update('email', e.target.value)}
                  placeholder="team@company.com"
                  disabled={formDisabled}
                  className={inputClassName}
                />
                <InlineFieldError message={fieldErrors.email} />
              </div>

              <div>
                <label htmlFor="create-team-country" className="text-gray-700 text-xs font-normal mb-1 block">
                  Country / Region
                </label>
                <CountrySelect
                  id="create-team-country"
                  value={form.country ?? DEFAULT_TEAM_COUNTRY}
                  onChange={updateCountry}
                  disabled={formDisabled}
                />
              </div>

              <div>
                <label className="text-gray-700 text-xs font-normal mb-1 block">
                  Registered Address
                </label>
                <div
                  className={`w-full h-8 px-2.5 rounded-md border border-gray-200 bg-white flex items-center focus-within:border-green-500 ${
                    formDisabled ? 'bg-gray-50' : ''
                  }`}
                >
                  <GooglePlacesAutocomplete
                    id="create-team-address"
                    key={form.country ?? DEFAULT_TEAM_COUNTRY}
                    value={form.address ?? ''}
                    onChange={(address) => update('address', address)}
                    disabled={formDisabled}
                    country={form.country ?? DEFAULT_TEAM_COUNTRY}
                    placeholder="Start typing to search addresses…"
                    className="h-full"
                  />
                </div>
              </div>

              <div>
                <label className="text-gray-700 text-xs font-normal mb-1 block">About</label>
                <textarea
                  value={form.team_profile ?? ''}
                  onChange={(e) => update('team_profile', e.target.value)}
                  rows={2}
                  placeholder="Brief team description"
                  disabled={formDisabled}
                  className="w-full px-2.5 py-1.5 rounded-md border border-gray-200 text-sm outline-none focus:border-green-500 resize-none disabled:bg-gray-50 disabled:text-gray-400"
                />
              </div>

            </div>

            {trialEligible && (
              <div className="bg-green-50 rounded-lg border border-green-200 p-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-flask-line text-green-600 text-lg" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-900 font-semibold text-sm">1-month free trial</div>
                    <p className="text-gray-500 text-xs mt-1 leading-relaxed">
                      Creating this team will activate your one-time experience plan. Available only
                      the first time you create a team.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              <div className="mx-auto w-full" style={{ maxWidth: 480 }}>
                <button
                  type="button"
                  onClick={() => void handleSubmit()}
                  disabled={submitting || formDisabled}
                  className="w-full h-11 rounded-lg bg-green-500 hover:bg-green-600 text-white font-normal text-sm cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <i className="ri-team-line text-base" />
                      Create Team
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
